/**
 * Dashboard — KPI cards, live activity feed, opens-over-time chart, and the
 * app-version adoption card (which build each dealer last opened).
 * KPIs derive from users + activity_logs + app_config (all realtime).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import {
  useUsers,
  usePendingUsers,
  useRecentActivity,
  useActivitySince,
  useAppConfig,
} from '../hooks/useFirestore';
import { StatCard } from '../components/StatCard';
import { Badge, Card, SectionHeader } from '../components/ui';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { appVersionSummary, opensByDay } from '../lib/analytics';
import { AppVersionBreakdown } from '../components/AppVersionBreakdown';
import { describeLog, EVENT_LABEL } from '../lib/activityMeta';
import { startOfToday, daysAgo, dayLabel } from '../lib/time';
import { formatIndianNumber, timeAgo } from '../lib/format';
import { INSIGHTS } from '@mitang/shared';

const CHART_DAYS = 14;

export function DashboardPage(): JSX.Element {
  const users = useUsers();
  const pending = usePendingUsers();
  const recent = useRecentActivity(30);

  const todayStart = useMemo(() => startOfToday(), []);
  const weekStart = useMemo(() => daysAgo(INSIGHTS.activeWindowDays), []);
  const chartStart = useMemo(() => daysAgo(CHART_DAYS), []);

  const todayActivity = useActivitySince(todayStart);
  const weekActivity = useActivitySince(chartStart); // covers both week + chart windows

  const approvedCount = useMemo(
    () => users.data.filter((u) => u.status === 'approved').length,
    [users.data],
  );

  const activeToday = useMemo(() => {
    const ids = new Set<string>();
    for (const l of todayActivity.data) ids.add(l.userId);
    return ids.size;
  }, [todayActivity.data]);

  const opensThisWeek = useMemo(
    () => weekActivity.data.filter((l) => l.event === 'app_open' && l.ts >= weekStart).length,
    [weekActivity.data, weekStart],
  );

  const chartData = useMemo(
    () => opensByDay(weekActivity.data, CHART_DAYS).map((d) => ({ ...d, label: dayLabel(d.day) })),
    [weekActivity.data],
  );

  const kpiError = users.error ?? pending.error ?? todayActivity.error ?? weekActivity.error;

  const appConfig = useAppConfig();
  const latestPlayBuild = appConfig.data?.apk?.playVersionCode;
  const versions = useMemo(
    () => appVersionSummary(users.data, latestPlayBuild),
    [users.data, latestPlayBuild],
  );
  const recordedCount = users.data.length - versions.unrecorded;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Dashboard"
        subtitle="Live overview of dealers, approvals and app engagement."
      />

      {kpiError ? (
        <ErrorState message={kpiError} />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label="Approved dealers"
            value={formatIndianNumber(approvedCount)}
            tone="emerald"
            loading={users.loading}
            sublabel={`${users.data.length} total accounts`}
          />
          <StatCard
            label="Pending requests"
            value={formatIndianNumber(pending.data.length)}
            tone="amber"
            loading={pending.loading}
            sublabel="Awaiting review"
          />
          <StatCard
            label="Active today"
            value={formatIndianNumber(activeToday)}
            tone="brand"
            loading={todayActivity.loading}
            sublabel="Unique users since midnight"
          />
          <StatCard
            label={`Opens (${INSIGHTS.activeWindowDays}d)`}
            value={formatIndianNumber(opensThisWeek)}
            loading={weekActivity.loading}
            sublabel="App opens this week"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">App opens over time</h2>
              <p className="text-xs text-slate-500">Last {CHART_DAYS} days</p>
            </div>
          </div>
          <div className="h-64 p-4">
            {weekActivity.loading ? (
              <LoadingState label="Loading chart…" />
            ) : chartData.every((d) => d.opens === 0) ? (
              <EmptyState
                title="No app opens recorded yet"
                hint="Data appears as dealers use the app."
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="opensFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                    labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="opens"
                    name="App opens"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    fill="url(#opensFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-900">Live activity</h2>
            <p className="text-xs text-slate-500">Most recent events, realtime</p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {recent.loading ? (
              <div className="p-4">
                <LoadingState label="Loading feed…" />
              </div>
            ) : recent.error ? (
              <div className="p-4">
                <ErrorState message={recent.error} />
              </div>
            ) : recent.data.length === 0 ? (
              <div className="p-4">
                <EmptyState title="No recent activity" />
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {recent.data.map((log) => (
                  <li key={log.id} className="flex items-start gap-3 px-5 py-3">
                    <span
                      aria-hidden
                      className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-brand-400"
                      title={EVENT_LABEL[log.event]}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-slate-700">{describeLog(log)}</p>
                      <p className="truncate text-[11px] text-slate-400">
                        {log.userId} · {timeAgo(log.ts)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">App versions</h2>
            <p className="text-xs text-slate-500">
              Which build each dealer opened last · fills in as they open the app
            </p>
          </div>
          <div className="flex items-center gap-3">
            {latestPlayBuild !== undefined && (
              <Badge tone="brand">Latest Play build {latestPlayBuild}</Badge>
            )}
            <Link to="/versions" className="text-xs font-medium text-brand-600 hover:underline">
              All dealers →
            </Link>
          </div>
        </div>
        {users.loading ? (
          <div className="p-4">
            <LoadingState label="Loading versions…" />
          </div>
        ) : recordedCount === 0 ? (
          <div className="p-4">
            <EmptyState
              title="No app versions recorded yet"
              hint="Each dealer's build is stamped the next time they open the app."
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 px-5 py-5 md:grid-cols-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Updated via Play Store
              </p>
              <p className="mt-1 text-3xl font-semibold tracking-tight text-emerald-600">
                {formatIndianNumber(versions.playOnLatest)}
                <span className="text-base font-medium text-slate-400">
                  {' '}
                  / {formatIndianNumber(versions.playTotal)}
                </span>
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {versions.playBehind === 0
                  ? 'Every Play install seen is on the latest build'
                  : `${formatIndianNumber(versions.playBehind)} still on an older Play build`}
              </p>
              <p className="mt-3 text-xs text-slate-500">
                {formatIndianNumber(versions.unrecorded)} of {formatIndianNumber(users.data.length)}{' '}
                accounts have not opened the app since tracking began.
              </p>
            </div>
            <div className="md:col-span-2">
              <AppVersionBreakdown rows={versions.rows} total={users.data.length} />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
